import { DriverDetailsStep } from "@/D2C/features/car-quote/components/pre-quote/DriverDetailsStep";
import { PersonalDetailsStep as CarPersonalDetailsStep } from "@/D2C/features/car-quote/components/pre-quote/PersonalDetailsStep";
import { VehicleDetailsStep } from "@/D2C/features/car-quote/components/pre-quote/VehicleDetailsStep";
import {
  defaultDriverFormValues,
  defaultPersonalFormValues,
  defaultVehicleFormValues,
} from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import type {
  CarDriverFormValues,
  CarPersonalFormValues,
  CarVehicleFormValues,
} from "@/D2C/features/car-quote/redux/type";
import { HealthDetailsStep } from "@/D2C/features/health-quote/components/pre-quote/HealthDetailsStep";
import { HealthHabitsStep } from "@/D2C/features/health-quote/components/pre-quote/HealthHabitsStep";
import { HealthQuickQuestionsStep } from "@/D2C/features/health-quote/components/pre-quote/HealthQuickQuestionsStep";
import { PersonalDetailsStep as HealthPersonalDetailsStep } from "@/D2C/features/health-quote/components/pre-quote/PersonalDetailsStep";
import {
  defaultHealthDetailsFormValues,
  defaultHealthHabitsFormValues,
  defaultHealthPersonalFormValues,
  defaultHealthQuickQuestionsFormValues,
} from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import type {
  HealthDetailsFormValues,
  HealthHabitsFormValues,
  HealthPersonalFormValues,
  HealthQuickQuestionsFormValues,
} from "@/D2C/features/health-quote/redux/type";
import { LifePlanDetailsStep } from "@/D2C/features/life-quote/components/pre-quote/TermInsuranceDetailsStep";
import {
  defaultLifePlanDetailsFormValues,
  type LifePlanDetailsFormValues,
} from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import { CommonAccordion } from "@/D2C/features/travel-quote/components/post-quote/TravellerAccordion";
import { PersonalDetailsStep as TravelPersonalDetailsStep } from "@/D2C/features/travel-quote/components/pre-quote/PersonalDetailsStep";
import { TravelDetailsStep } from "@/D2C/features/travel-quote/components/pre-quote/TravelDetailsStep";
import {
  defaultTravelDetailsFormValues,
  defaultTravelPersonalFormValues,
} from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import type {
  TravelDetailsFormValues,
  TravelPersonalFormValues,
} from "@/D2C/features/travel-quote/redux/type";
import { LOB_IDS } from "@/shared/common/static";
import { useEffect, useState } from "react";
import type { AddQuoteProduct } from "../config/quoteProducts.config";
import type { useQuoteRefs } from "../hooks/useQuoteRefs";
import { CompactQuoteFormStyles } from "./CompactQuoteFormStyles";

type Refs = ReturnType<typeof useQuoteRefs>;

type PreQuoteDefaultValues = {
  travelPersonal?: TravelPersonalFormValues;
  travelDetails?: TravelDetailsFormValues;
  carPersonal?: CarPersonalFormValues;
  carVehicle?: CarVehicleFormValues;
  carDriver?: CarDriverFormValues;
  healthPersonal?: HealthPersonalFormValues;
  healthDetails?: HealthDetailsFormValues;
  healthHabits?: HealthHabitsFormValues;
  healthQuickQuestions?: HealthQuickQuestionsFormValues;
  lifePersonal?: TravelPersonalFormValues;
  lifePlanDetails?: LifePlanDetailsFormValues;
};

type Props = {
  product: AddQuoteProduct & { lobId: number };
  refs: Refs;
  isReadOnly?: boolean;
  defaultValues?: PreQuoteDefaultValues;
  healthAccordionOpenVersion?: number;
  healthAccordionInvalidSections?: Partial<HealthAccordionState>;
  forceCloseAccordions?: boolean;
};

type HealthAccordionState = {
  basic: boolean;
  habits: boolean;
  quick: boolean;
};

export function PreQuoteForms({
  product,
  refs,
  isReadOnly = false,
  defaultValues,
  healthAccordionOpenVersion,
  healthAccordionInvalidSections,
  forceCloseAccordions,
}: Props) {
  const [isMotorAccordionOpen, setIsMotorAccordionOpen] = useState(true);
  const [isLifeAccordionOpen, setIsLifeAccordionOpen] = useState(true);
  const [b2cLivePrimaryMemberName, setB2cLivePrimaryMemberName] = useState("");
  const [healthAccordions, setHealthAccordions] = useState<HealthAccordionState>({
    basic: true,
    habits: false,
    quick: false,
  });

  useEffect(() => {
    if (healthAccordionOpenVersion === undefined) {
      return;
    }

    if (!healthAccordionInvalidSections) {
      return;
    }

    setHealthAccordions({
      basic: Boolean(healthAccordionInvalidSections.basic),
      habits: Boolean(healthAccordionInvalidSections.habits),
      quick: Boolean(healthAccordionInvalidSections.quick),
    });
  }, [healthAccordionInvalidSections, healthAccordionOpenVersion]);

  useEffect(() => {
    if (!forceCloseAccordions) {
      return;
    }

    setIsMotorAccordionOpen(false);
    setIsLifeAccordionOpen(false);
    setHealthAccordions({
      basic: false,
      habits: false,
      quick: false,
    });
  }, [forceCloseAccordions]);

  const toggleHealthAccordion = (section: keyof HealthAccordionState) => {
    if (isReadOnly) {
      return;
    }

    setHealthAccordions((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <fieldset disabled={isReadOnly} className="b2c-compact-quote-form space-y-2">
      <CompactQuoteFormStyles />
      {product.lobId === LOB_IDS.TRAVEL ? (
        <>
          <TravelPersonalDetailsStep
            ref={refs.travelPersonalRef}
            defaultValues={defaultValues?.travelPersonal ?? defaultTravelPersonalFormValues}
            layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-4 xl:grid-cols-4"
            showAdditionalFields={false}
          />

          <TravelDetailsStep
            ref={refs.travelDetailsRef}
            defaultValues={defaultValues?.travelDetails ?? defaultTravelDetailsFormValues}
            layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            additionalTravellersLayout="grid"
            countryFieldsLayout="inline"
            travellerActionButtonsClassName="flex items-start gap-2 md:pt-5"
            disableSharedPrimaryTravellerAutofill
          />
        </>
      ) : null}

      {product.lobId === LOB_IDS.MOTOR ? (
        <>
          <CarPersonalDetailsStep
            ref={refs.carPersonalRef}
            defaultValues={defaultValues?.carPersonal ?? defaultPersonalFormValues}
            layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-4"
            showAdditionalFields={false}
          />
          <CommonAccordion
            isOpen={isMotorAccordionOpen}
            title="Vehicle Details"
            keepMounted
            onToggle={() => {
              if (isReadOnly) {
                return;
              }
              setIsMotorAccordionOpen((prev) => !prev);
            }}
          >
            <VehicleDetailsStep
              ref={refs.carVehicleRef}
              defaultValues={defaultValues?.carVehicle ?? defaultVehicleFormValues}
              layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4"
            />

            <DriverDetailsStep
              ref={refs.carDriverRef}
              defaultValues={defaultValues?.carDriver ?? defaultDriverFormValues}
              layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            />
          </CommonAccordion>
        </>
      ) : null}

      {product.lobId === LOB_IDS.HEALTH ? (
        <>
          <HealthPersonalDetailsStep
            ref={refs.healthPersonalRef}
            defaultValues={defaultValues?.healthPersonal ?? defaultHealthPersonalFormValues}
            layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-4"
            showAdditionalFields={false}
            onValuesChange={(values) => {
              const primaryMemberName =
                `${values?.firstName || ""} ${values?.lastName || ""}`.trim();
              setB2cLivePrimaryMemberName(primaryMemberName);
            }}
          />

          <CommonAccordion
            isOpen={healthAccordions.basic}
            title="Basic Health Details"
            keepMounted
            onToggle={() => {
              toggleHealthAccordion("basic");
            }}
          >
            <HealthDetailsStep
              ref={refs.healthDetailsRef}
              defaultValues={defaultValues?.healthDetails ?? defaultHealthDetailsFormValues}
              layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
              additionalTravellersLayout="grid"
              showMemberDetailsTitle={false}
              portingPolicyLayoutClassName="col-span-full grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4"
              livePrimaryMemberName={b2cLivePrimaryMemberName}
            />
          </CommonAccordion>

          <CommonAccordion
            isOpen={healthAccordions.habits}
            title="Health Habits"
            keepMounted
            onToggle={() => {
              toggleHealthAccordion("habits");
            }}
          >
            <HealthHabitsStep
              ref={refs.healthHabitsRef}
              defaultValues={defaultValues?.healthHabits ?? defaultHealthHabitsFormValues}
              layoutClassName="space-y-2"
              habitSelectorLayoutClassName="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5"
              questionsLayoutClassName="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3"
              showHealthTitleAndDescription={false}
            />
          </CommonAccordion>

          <CommonAccordion
            isOpen={healthAccordions.quick}
            title="Health Quick Questions"
            keepMounted
            onToggle={() => {
              toggleHealthAccordion("quick");
            }}
          >
            <HealthQuickQuestionsStep
              ref={refs.healthQuickQuestionsRef}
              defaultValues={
                defaultValues?.healthQuickQuestions ?? defaultHealthQuickQuestionsFormValues
              }
              medicalConditionsLayoutClassName="grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-5"
              lifestyleLayoutClassName="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5"
              placeAdditionalInformationInLifestyleGrid
              additionalInformationClassName="md:col-span-2 lg:col-span-2 xl:col-span-2"
              showTitleAndDescription={false}
            />
          </CommonAccordion>
        </>
      ) : null}
      {product.lobId === LOB_IDS.LIFE ? (
        <>
          <TravelPersonalDetailsStep
            ref={refs.lifePersonalRef}
            defaultValues={defaultValues?.lifePersonal ?? defaultTravelPersonalFormValues}
            layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-4"
            showAdditionalFields={false}
          />
          <CommonAccordion
            isOpen={isLifeAccordionOpen}
            title="Life Plan Details"
            keepMounted
            onToggle={() => {
              if (isReadOnly) {
                return;
              }
              setIsLifeAccordionOpen((prev) => !prev);
            }}
          >
            <LifePlanDetailsStep
              ref={refs.lifePlanDetailsRef}
              defaultValues={defaultValues?.lifePlanDetails ?? defaultLifePlanDetailsFormValues}
              layoutClassName="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4"
            />
          </CommonAccordion>
        </>
      ) : null}
    </fieldset>
  );
}
